import type { Faq } from 'libs/http/api/content/content.types';

import type { FeatureGridItem } from 'ui/atoms/FeatureGrid/FeatureGrid';
import type { RegisMessage } from 'ui/molecules/RegisChat/RegisChat.types';
import type { ProcessStep } from 'ui/molecules/ProcessSteps/ProcessSteps.types';

type Translate = (key: string) => string;

// ── Editorial numbered features ─────────────────────────────────────────
export interface EditorialRow {
  num: string;
  title: string;
  description: string;
}

export const getEditorialRows = (t: Translate): EditorialRow[] => [
  {
    num: '01',
    title: t('Primary, government-sourced records'),
    description: t(
      'No inferred relationships and no third-party guesswork. Shareholder registers, PSC statements and officer appointments come directly from 400+ government registries — the same documents a regulator would check.',
    ),
  },
  {
    num: '02',
    title: t('UBO resolution to the natural person'),
    description: t(
      'Traverse every layer of holdcos and SPVs until the chain ends in a person — at 25%, 10% or whatever threshold your policy sets. Direct and indirect stakes are calculated separately.',
    ),
  },
  {
    num: '03',
    title: t('Cross-border corporate linkage'),
    description: t(
      'The graph connects filings from registries in 200+ countries, resolving the same entity and the same person across jurisdictions — every directorship and every stake in one view.',
    ),
  },
  {
    num: '04',
    title: t('Full source attribution on every edge'),
    description: t(
      'Each relationship carries the registry it came from, the filing date and a direct link to the source document. When a reviewer asks where a number came from, the answer is attached to it.',
    ),
  },
];

export const getStatlineItems = (t: Translate): FeatureGridItem[] => [
  { value: '600M', suffix: '+', title: t('companies in the graph') },
  { value: '400', suffix: '+', title: t('government registries') },
  { value: '200', suffix: '+', title: t('countries & jurisdictions') },
  { value: '100', suffix: '%', title: t('edges with source attribution') },
];

// ── How it works ────────────────────────────────────────────────────────
export const getProcessSteps = (t: Translate): ProcessStep[] => [
  {
    title: t('Start from a person or a company'),
    description: t(
      'An individual’s name — or a company name, registration number, VAT ID or LEI. Ask Regis in plain language or call the ownership API.',
    ),
  },
  {
    title: t('The network builds itself'),
    description: t(
      'Positions, shareholdings and officers are pulled layer by layer from each registry of record, percentages are calculated through the chain, and gaps or undisclosed holders are flagged — not papered over.',
    ),
  },
  {
    title: t('Screen, export or monitor'),
    description: t(
      'Run a sanctions & PEP screen across every node, export an evidenced audit file, or set monitoring so a new filing that changes the structure triggers an alert the day it lands.',
    ),
  },
];

// ── Use cases ───────────────────────────────────────────────────────────
export interface UseCase {
  tag: string;
  title: string;
  description: string;
}

export const getUseCases = (t: Translate): UseCase[] => [
  {
    tag: t('Compliance'),
    title: t('UBO discovery for AML'),
    description: t(
      'Meet AMLD, MLR and FinCEN beneficial-ownership obligations with registry-cited UBOs — and cut the manual chasing of shareholder documents out of every review.',
    ),
  },
  {
    tag: t('Risk'),
    title: t('Hidden exposure & networks'),
    description: t(
      'Pivot from any company to every entity its owners and officers touch. Surface links to sanctioned parties, high-risk jurisdictions or undisclosed group structures before you’re exposed to them.',
    ),
  },
  {
    tag: t('Data & Product'),
    title: t('Corporate linkage at scale'),
    description: t(
      'Embed ownership graphs and UBO data into your own product or entity master via API and bulk feeds, with source attribution preserved on every record.',
    ),
  },
];

// ── Screening / Connector checklists ────────────────────────────────────
export const getScreeningPoints = (t: Translate): string[] => [
  t('OFAC, EU, UN and UK HMT consolidated lists'),
  t('Covers affiliated individuals — including minority shareholders — not just the target entity'),
  t('Re-screened automatically when lists or registry filings change'),
  t('Results export with source citations for the audit file'),
];

export const getConnectorPoints = (t: Translate): string[] => [
  t('Free connector in the Anthropic directory'),
  t('Extension for ChatGPT'),
  t('Registry citation preserved on every answer'),
  t('REST API and bulk files for pipelines'),
];

// ── Regis conversation ──────────────────────────────────────────────────
export const REGIS_MESSAGES: RegisMessage[] = [
  {
    from: 'user',
    content: 'Who ultimately owns Kessler Trading Ltd?',
  },
  {
    from: 'assistant',
    content: (
      <>
        Kessler Trading Ltd is 100% owned by <b>Kessler Group Holdings Ltd</b>. The ultimate beneficial owner is{' '}
        <b>Martin Kessler</b>, with a <b>52.4% indirect stake</b> held through the holding company.
        <br />
        <small>Companies House · PSC register — filed 14 May 2026</small>
      </>
    ),
  },
];

// ── FAQ ─────────────────────────────────────────────────────────────────
export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'ubo-definition',
    question: t('What is an ultimate beneficial owner (UBO)?'),
    answer: t(
      'A UBO is the natural person who ultimately owns or controls a company, even when that control runs through layers of holding companies, SPVs or nominees. Most AML regimes define UBOs at a 25% ownership or control threshold, though many institutions apply 10% or lower. The ownership graph resolves chains to the natural person at whatever threshold your policy sets.',
    ),
    default_opened: true,
  },
  {
    id: 'person-companies',
    question: t('How do I find every company a person is connected to?'),
    answer: t(
      'Search the person’s name. The ownership graph returns every directorship, shareholding and beneficial-ownership position they hold across 200+ countries — resolved from official government registries, with each connection cited to its source filing.',
    ),
  },
  {
    id: 'data-source',
    question: t('Where does the ownership data come from?'),
    answer: t(
      'Directly from 400+ government registries — company registers, PSC and beneficial-ownership registers, and official filings. Every relationship carries the registry name, the filing date and a direct link to the source document. Nothing is modeled or inferred.',
    ),
  },
  {
    id: 'coverage',
    question: t('Which countries and companies are covered?'),
    answer: t(
      'The graph covers 600M+ companies across 200+ countries and jurisdictions, connected through registries such as Companies House (UK), CRO Ireland and the German Handelsregister, among hundreds of others.',
    ),
  },
  {
    id: 'indirect-ownership',
    question: t('Do you calculate indirect ownership percentages?'),
    answer: t(
      'Yes. Direct stakes come from the filings; indirect stakes are calculated through each layer of the chain, so a person holding 52.4% of a holding company that owns 100% of a subsidiary is shown with their effective position at every level.',
    ),
  },
  {
    id: 'network-screening',
    question: t('Can I screen an entire network for sanctions and PEPs?'),
    answer: t(
      'Yes. Every company and affiliated individual in a network — directors, shareholders and UBOs — can be screened in a single pass against OFAC, EU, UN and UK HMT consolidated lists, with flagged results highlighted for review.',
    ),
  },
  {
    id: 'audit-ready',
    question: t('Is the data audit-ready?'),
    answer: t(
      'Yes. Every edge in the graph carries its registry citation, filing date and source link, and results export with that evidence attached — so a reviewer or regulator can trace any number back to the official filing it came from.',
    ),
  },
  {
    id: 'vs-modeled',
    question: t('How is this different from providers that model ownership data?'),
    answer: t(
      'Modeled or inferred ownership collapses under audit because there is no source document behind it. Global Database returns primary, government-sourced records — the same documents a regulator would check — with the citation attached to every relationship.',
    ),
  },
  {
    id: 'api-connectors',
    question: t('Can I access the ownership graph through an API or inside Claude and ChatGPT?'),
    answer: t(
      'Yes. There is a REST API and bulk delivery for pipelines, and free connectors for Claude and ChatGPT, so teams can query ownership data in plain language inside the assistant they already use — with registry citations preserved on every answer.',
    ),
  },
  {
    id: 'freshness',
    question: t('How current is the data?'),
    answer: t(
      'Structures are resolved from the registry of record, and monitoring can alert you when a new filing changes a structure — so a resignation, share transfer or status change surfaces when it lands rather than at the next periodic review.',
    ),
  },
];
